import React, { useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import { Redirect, useParams } from "react-router-dom";
import moment from "moment";

import { Blockquote, Button, Modal, Module, ModuleBody } from "@wfp/ui";

import ModalExtended from "components/ModalExtended";
import ButtonRouter from "components/ButtonRouter";
import CommodityTextView from "components/CommodityTextView";
import ErrorList from "components/ErrorList";
import MeasureUnitValue from "components/MeasureUnitValue";
import QuantityInput from "components/QuantityInput";
import SubTitle from "components/SubTitle";
import TableExtended from "components/TableExtended";
import YearEditIncidentReasons from "components/YearEditIncidentReasons";
import { Repeater, RepeaterItem, RepeaterTitle } from "components/Repeater";

import useStore from "helpers/useStore";
import {
  getInitialCarryOverPurchaseStock,
  getInitialCarryOverStock,
  getInitialPurchaseStockIncidents,
  getInitialStockIncidents,
} from "helpers/stock";
import commoditiesDuck from "data-handler/ducks/commodities";
import {
  getAllSchoolYears,
  getAllStores,
  getIsSyncableLocked,
  isDeliveryStore,
  isPurchaseDetailStore,
  resetStore,
  SchoolYear,
  Store,
} from "data-handler/ducks/stores";
import { deliveryCategory, purchaseDetailCategory } from "SCConstants";
import { RootState } from "data-handler/rootReducer";

import {
  buildCarryOverDeliveryIncidentEvents,
  buildCarryOverPurchaseIncidentEvents,
  reconcileInitialStockIncidents,
  toQuantity,
} from "./carryOverIncidents";

import "./styles.scss";

type CarryOverEditProps = {
  currentStoreData?: SchoolYear;
};

/**
 * Modal which manages the carry-over stock of a school year.
 *
 * While the school year has not been synced yet, quantities can be lowered,
 * which records "initial stock" incidents against the deliveries/purchases
 * of the previous school year. Once the school year has been synced, the
 * modal becomes read-only and links to the source deliveries/purchases,
 * which are then the only place where the stock can still be corrected.
 */
const CarryOverEdit: React.ElementType<CarryOverEditProps> = ({
  currentStoreData,
}) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const params: Record<string, string> = useParams();
  const {
    updateStore,
    closeModal,
    submitBtnRef,
    submitViaBtnRefClick,
  } = useStore();

  const yearPath = `/school/${params.schoolId}/info/${params.item}`;

  const allStores = useSelector(getAllStores);
  const allSchoolYears = useSelector(getAllSchoolYears);
  const commodityIndex = useSelector(commoditiesDuck.getIndex);

  const isLocked = useSelector((state: RootState) =>
    getIsSyncableLocked(state, currentStoreData)
  );

  const carryOverDate = currentStoreData?.starts_on;

  const initialCarryOverStock = useSelector((state: RootState) =>
    getInitialCarryOverStock(state, carryOverDate)
  );
  const initialCarryOverPurchaseStock = useSelector((state: RootState) =>
    getInitialCarryOverPurchaseStock(state, carryOverDate)
  );
  const initialStockIncidents = useSelector((state: RootState) =>
    getInitialStockIncidents(state, carryOverDate)
  );
  const initialPurchaseStockIncidents = useSelector((state: RootState) =>
    getInitialPurchaseStockIncidents(state, carryOverDate)
  );

  const isSynced = Boolean(currentStoreData?.id);
  const isReadOnly = isSynced || isLocked;

  // Synced deliveries/purchases expose their remaining balance, which already
  // discounts synced incidents. Unsynced incidents are not discounted yet.
  const buildQuantityMaps = (carryOverStock: any, incidents: any) => {
    const initialByCommodity: Record<string, number> = {};
    const adjustedByCommodity: Record<string, number> = {};
    const incidentsByCommodity: Record<string, number> = {};
    // A commodity fully consumed by synced incidents no longer has remaining
    // stock, but must still be listed with its incidents
    const commodityIds = new Set([
      ...Object.keys(carryOverStock),
      ...Object.keys(incidents.quantityPerCommodity),
    ]);
    commodityIds.forEach((commodityId) => {
      const total = toQuantity(carryOverStock[commodityId]?.totalQuantity);
      const incidentQuantity = toQuantity(
        incidents.quantityPerCommodity[commodityId]?.totalQuantity
      );
      initialByCommodity[commodityId] = isSynced
        ? toQuantity(total + incidentQuantity)
        : total;
      adjustedByCommodity[commodityId] = isSynced
        ? total
        : toQuantity(total - incidentQuantity);
      incidentsByCommodity[commodityId] = incidentQuantity;
    });
    return { initialByCommodity, adjustedByCommodity, incidentsByCommodity };
  };

  const {
    initialByCommodity: initialQuantities,
    adjustedByCommodity: adjustedQuantities,
    incidentsByCommodity: incidentQuantities,
  } = buildQuantityMaps(initialCarryOverStock, initialStockIncidents);

  const {
    initialByCommodity: initialPurchaseQuantities,
    adjustedByCommodity: adjustedPurchaseQuantities,
    incidentsByCommodity: purchaseIncidentQuantities,
  } = buildQuantityMaps(
    initialCarryOverPurchaseStock,
    initialPurchaseStockIncidents
  );

  const commoditiesList = Object.keys(initialCarryOverStock)
    .map((commodityId) => ({
      commodity: parseInt(commodityId),
      quantity: adjustedQuantities[commodityId],
      measure_unit: initialCarryOverStock[commodityId].measure_unit,
      category: deliveryCategory,
    }))
    .filter((item) => item.quantity >= 0);

  const purchaseCommoditiesList = Object.keys(initialCarryOverPurchaseStock)
    .map((commodityId) => ({
      commodity: parseInt(commodityId),
      quantity: adjustedPurchaseQuantities[commodityId],
      measure_unit: initialCarryOverPurchaseStock[commodityId].measure_unit,
      category: purchaseDetailCategory,
    }))
    .filter((item) => item.quantity >= 0);

  const updatedIncidentQuantities = useRef<Record<string, number>>({
    ...incidentQuantities,
  });
  const updatedPurchaseIncidentQuantities = useRef<Record<string, number>>({
    ...purchaseIncidentQuantities,
  });
  const showIncidentForCommodity = useRef<Record<string, boolean>>({});
  const showQuantityExceededWarning = useRef<Record<string, boolean>>({});

  // Rerender after programmatic quantity changes, so the ref-driven incident
  // sections and the zero-all button states update
  const [, setRenderTick] = useState(0);

  // Which category's "Set all to zero" is awaiting confirmation
  const [zeroAllTarget, setZeroAllTarget] = useState<
    "commodities" | "purchaseCommodities" | null
  >(null);

  // Produces flat error keys (like the YearEdit resolver does), which is what
  // ErrorList and YearEditIncidentReasons expect
  const carryOverValidationResolver = (values: any) => {
    const validationErrors: Record<string, any> = {};

    const validateRows = (
      rows: any[],
      fieldArrayName: string,
      initialQuantitiesMap: Record<string, number>,
      adjustedQuantitiesMap: Record<string, number>,
      exceededMessage: React.ReactNode
    ) => {
      (rows || []).forEach((row: any, index: number) => {
        if (!row?.commodity) return;
        const quantity = toQuantity(row.quantity);
        if (quantity > (initialQuantitiesMap[row.commodity] || 0)) {
          validationErrors[`${fieldArrayName}[${index}].quantity`] = {
            message: exceededMessage,
          };
        } else if (
          quantity < (adjustedQuantitiesMap[row.commodity] || 0) &&
          !row.reason
        ) {
          validationErrors[`${fieldArrayName}[${index}].reason`] = {
            message: (
              <FormattedMessage
                id="YearEditError.missingIncidentType"
                defaultMessage="Select an incident type"
              />
            ),
          };
        }
      });
    };

    validateRows(
      values.commodities,
      "commodities",
      initialQuantities,
      adjustedQuantities,
      <FormattedMessage
        id="YearEdit.quantityExceededWarning"
        defaultMessage="You cannot increase the quantity of a carry-over stock."
      />
    );
    validateRows(
      values.purchaseCommodities,
      "purchaseCommodities",
      initialPurchaseQuantities,
      adjustedPurchaseQuantities,
      <FormattedMessage
        id="YearEdit.quantityExceededPurchaseWarning"
        defaultMessage="You cannot increase the quantity of a carry-over purchase stock."
      />
    );

    return {
      values: Object.keys(validationErrors).length > 0 ? {} : values,
      errors: validationErrors,
    };
  };

  const {
    control,
    handleSubmit,
    errors,
    register,
    watch,
    getValues,
    setValue,
  } = useForm({
    defaultValues: {
      commodities: commoditiesList,
      purchaseCommodities: purchaseCommoditiesList,
    },
    resolver: carryOverValidationResolver,
  });

  const { fields: commodityRows } = useFieldArray({
    control,
    name: "commodities",
  });
  const { fields: purchaseCommodityRows } = useFieldArray({
    control,
    name: "purchaseCommodities",
  });

  const setStockQuantity = (
    commodity: any,
    warnKey: string | number,
    value: number,
    initialQuantity: number,
    adjustedQuantity: number,
    incidentQuantitiesRef: React.MutableRefObject<Record<string, number>>
  ) => {
    switch (true) {
      // Carry-over stock can never be increased above its initial quantity
      case value > initialQuantity:
        showQuantityExceededWarning.current = {
          ...showQuantityExceededWarning.current,
          [warnKey]: true,
        };
        showIncidentForCommodity.current = {
          ...showIncidentForCommodity.current,
          [warnKey]: false,
        };
        break;
      // Between the adjusted and the initial quantity no new incident is needed
      // (existing incidents shrink on save instead)
      case value <= initialQuantity && value >= adjustedQuantity:
        showQuantityExceededWarning.current = {
          ...showQuantityExceededWarning.current,
          [warnKey]: false,
        };
        showIncidentForCommodity.current = {
          ...showIncidentForCommodity.current,
          [warnKey]: false,
        };
        break;
      // Below the adjusted quantity an incident (with a reason) is required
      case value < adjustedQuantity:
        showQuantityExceededWarning.current = {
          ...showQuantityExceededWarning.current,
          [warnKey]: false,
        };
        showIncidentForCommodity.current = {
          ...showIncidentForCommodity.current,
          [warnKey]: true,
        };
        break;
      default:
        break;
    }

    const cappedValue = Math.min(value, initialQuantity);
    incidentQuantitiesRef.current = {
      ...incidentQuantitiesRef.current,
      [commodity]: toQuantity(initialQuantity - cappedValue),
    };
  };

  const currentQuantity = (
    fieldArrayName: "commodities" | "purchaseCommodities",
    row: any,
    index: number
  ) => {
    const formRows: any = getValues()[fieldArrayName] || [];
    return toQuantity(formRows[index]?.quantity ?? row.quantity);
  };

  const areAllQuantitiesZero = (
    fieldArrayName: "commodities" | "purchaseCommodities",
    rows: any[]
  ) =>
    rows.every(
      (row: any, index) => currentQuantity(fieldArrayName, row, index) === 0
    );

  const zeroAllQuantities = (
    fieldArrayName: "commodities" | "purchaseCommodities",
    rows: any[],
    initialQuantitiesMap: Record<string, number>,
    adjustedQuantitiesMap: Record<string, number>,
    incidentQuantitiesRef: React.MutableRefObject<Record<string, number>>
  ) => {
    rows.forEach((row: any, index) => {
      setValue(`${fieldArrayName}[${index}].quantity`, 0);
      setStockQuantity(
        row.commodity,
        fieldArrayName === "purchaseCommodities" ? `purchase_${index}` : index,
        0,
        initialQuantitiesMap[row.commodity] || 0,
        adjustedQuantitiesMap[row.commodity] || 0,
        incidentQuantitiesRef
      );
    });
    setRenderTick((tick) => tick + 1);
  };

  const confirmZeroAll = () => {
    if (zeroAllTarget === "commodities") {
      zeroAllQuantities(
        "commodities",
        commodityRows,
        initialQuantities,
        adjustedQuantities,
        updatedIncidentQuantities
      );
    } else if (zeroAllTarget === "purchaseCommodities") {
      zeroAllQuantities(
        "purchaseCommodities",
        purchaseCommodityRows,
        initialPurchaseQuantities,
        adjustedPurchaseQuantities,
        updatedPurchaseIncidentQuantities
      );
    }
    setZeroAllTarget(null);
  };

  const showInitialQuantityLabel = (
    commodity: any,
    index: number,
    fieldArrayName: "commodities" | "purchaseCommodities",
    initialQuantitiesMap: Record<string, number>,
    adjustedQuantitiesMap: Record<string, number>
  ) => {
    const formRows: any = getValues()[fieldArrayName] || [];
    const currentQuantity = toQuantity(
      formRows[index]?.quantity ?? adjustedQuantitiesMap[commodity]
    );
    return (initialQuantitiesMap[commodity] || 0) !== currentQuantity;
  };

  const findStoreByStockEventId = (
    eventId: number | undefined,
    isStoreOfKind: (store: Store) => boolean
  ) =>
    eventId === undefined
      ? undefined
      : allStores.find(
          (store: any) =>
            isStoreOfKind(store) &&
            store.type !== "delete" &&
            (store.stock_events || []).some(
              (stockEvent: any) => stockEvent.id === eventId
            )
        );

  const getSourceDeliveries = (commodityId: string) => {
    const quantityPerBatchNo =
      initialCarryOverStock[commodityId]?.quantityPerBatchNo || {};
    const deliveryCommodityIds: number[] = Object.keys(
      quantityPerBatchNo
    ).flatMap((batchNo) =>
      (quantityPerBatchNo[batchNo]?.deliveryCommodityDetails || []).map(
        (detail: any) => detail.deliveryCommodityId
      )
    );
    const sources: Store[] = [];
    deliveryCommodityIds.forEach((eventId) => {
      const store: any = findStoreByStockEventId(eventId, isDeliveryStore);
      if (store && !sources.find((s: any) => s.client_id === store.client_id)) {
        sources.push(store);
      }
    });
    return sources;
  };

  const getSourcePurchases = (commodityId: string) => {
    const purchases =
      initialCarryOverPurchaseStock[commodityId]?.purchases || [];
    const sources: Store[] = [];
    purchases.forEach((purchase: any) => {
      const store: any = findStoreByStockEventId(
        purchase.purchaseCommodityId,
        isPurchaseDetailStore
      );
      if (store && !sources.find((s: any) => s.client_id === store.client_id)) {
        sources.push(store);
      }
    });
    return sources;
  };

  const getIncidentsForCommodity = (incidentObjects: any[]) => (
    commodityId: string
  ) =>
    incidentObjects.filter((incident: any) =>
      (incident.stock_events || []).some(
        (stockEvent: any) =>
          stockEvent.commodity?.toString() === commodityId.toString()
      )
    );

  const applyIncidentPlan = (
    plan: {
      toCreate: any[];
      toUpdate: { client_id: string; values: any }[];
      toRemove: any[];
    },
    category: string,
    date: string
  ) => {
    let dispatched = false;
    plan.toRemove.forEach((store: any) => {
      if (store.sync === false && !store.id) {
        dispatch(resetStore(store.client_id));
      } else {
        updateStore(
          {
            id: store.client_id,
            values: { ...store, occurred_at: date },
            type: "delete",
          },
          yearPath
        );
        dispatched = true;
      }
    });
    plan.toUpdate.forEach(({ client_id, values }) => {
      updateStore(
        { id: client_id, values, type: "update", category },
        yearPath
      );
      dispatched = true;
    });
    plan.toCreate.forEach((values) => {
      updateStore(
        { id: undefined, values, type: "create", category },
        yearPath
      );
      dispatched = true;
    });
    return dispatched;
  };

  const onSubmit = (formValues: any) => {
    const date = moment(currentStoreData!.starts_on).format("YYYY-MM-DD");

    const deliveryPlan = reconcileInitialStockIncidents({
      rows: (formValues.commodities || []).filter((c: any) => c.commodity),
      initialQuantities,
      adjustedQuantities,
      existingIncidents: initialStockIncidents.incidentObjects,
      buildEventsForCommodity: (commodity, quantity) =>
        buildCarryOverDeliveryIncidentEvents(
          initialCarryOverStock[commodity],
          commodity,
          quantity
        ),
      occurredAt: date,
      category: deliveryCategory,
    });

    const purchasePlan = reconcileInitialStockIncidents({
      rows: (formValues.purchaseCommodities || []).filter(
        (c: any) => c.commodity
      ),
      initialQuantities: initialPurchaseQuantities,
      adjustedQuantities: adjustedPurchaseQuantities,
      existingIncidents: initialPurchaseStockIncidents.incidentObjects,
      buildEventsForCommodity: (commodity, quantity) =>
        buildCarryOverPurchaseIncidentEvents(
          initialCarryOverPurchaseStock[commodity],
          commodity,
          quantity
        ),
      occurredAt: date,
      category: purchaseDetailCategory,
    });

    const dispatchedDeliveryIncidents = applyIncidentPlan(
      deliveryPlan,
      deliveryCategory,
      date
    );
    const dispatchedPurchaseIncidents = applyIncidentPlan(
      purchasePlan,
      purchaseDetailCategory,
      date
    );

    if (!dispatchedDeliveryIncidents && !dispatchedPurchaseIncidents) {
      closeModal(undefined, yearPath);
    }
  };

  // Carry-over only exists for school years which have a predecessor
  const isFirstSchoolYear =
    currentStoreData === undefined ||
    !allSchoolYears.some(
      (year: SchoolYear) =>
        year.client_id !== currentStoreData.client_id &&
        year.type !== "delete" &&
        moment(year.starts_on).isBefore(currentStoreData.starts_on, "day")
    );

  if (!currentStoreData || currentStoreData.model !== "year") {
    return <Redirect to={`/school/${params.schoolId}/info`} />;
  }
  if (isFirstSchoolYear) {
    return <Redirect to={yearPath} />;
  }

  const commodityName = (commodityId: any) =>
    commodityIndex[commodityId]?.name || `Unidentified(${commodityId})`;

  const renderReadOnlyTable = (
    titleMessage: React.ReactNode,
    rows: { commodity: string; measureUnit: any }[],
    initialQuantitiesMap: Record<string, number>,
    incidentQuantitiesMap: Record<string, number>,
    remainingQuantitiesMap: Record<string, number>,
    getIncidents: (commodityId: string) => Store[],
    getSources: (commodityId: string) => Store[],
    sourceLabel: (store: any) => React.ReactNode
  ) =>
    rows.length > 0 && (
      <div>
        <SubTitle>{titleMessage}</SubTitle>
        <Module noMargin>
          <ModuleBody noPadding>
            <TableExtended noBorder>
              <thead>
                <tr>
                  <th>
                    <FormattedMessage
                      id="CommoditySelect.commodityInputLabel"
                      defaultMessage="Commodity"
                    />
                  </th>
                  <th>
                    <FormattedMessage
                      id="CarryOverEdit.initialCarryOverQuantity"
                      defaultMessage="Initial carry-over quantity"
                    />
                  </th>
                  <th>
                    <FormattedMessage
                      id="YearEdit.incidentsQuantity"
                      defaultMessage="Incidents quantity"
                    />
                  </th>
                  <th>
                    <FormattedMessage
                      id="CarryOverEdit.remainingCarryOverQuantity"
                      defaultMessage="Remaining carry-over quantity"
                    />
                  </th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.commodity}>
                    <td>{commodityName(row.commodity)}</td>
                    <td>
                      <MeasureUnitValue measureUnit={row.measureUnit}>
                        {initialQuantitiesMap[row.commodity] || 0}
                      </MeasureUnitValue>
                    </td>
                    <td>
                      <MeasureUnitValue measureUnit={row.measureUnit}>
                        {incidentQuantitiesMap[row.commodity] || 0}
                      </MeasureUnitValue>
                    </td>
                    <td>
                      <MeasureUnitValue measureUnit={row.measureUnit}>
                        {Math.max(
                          0,
                          remainingQuantitiesMap[row.commodity] || 0
                        )}
                      </MeasureUnitValue>
                    </td>
                    <td className="carry-over-edit__sources">
                      {(incidentQuantitiesMap[row.commodity] || 0) > 0
                        ? getIncidents(row.commodity).map((store: any) => (
                            <ButtonRouter
                              key={store.client_id}
                              kind="ghost"
                              small
                              to={`/school/${params.schoolId}/deliveries/${store.client_id}`}
                            >
                              <FormattedMessage
                                id="CarryOverEdit.viewIncident"
                                defaultMessage="View incident"
                              />
                            </ButtonRouter>
                          ))
                        : getSources(row.commodity).map((store: any) => (
                            <ButtonRouter
                              key={store.client_id}
                              kind="ghost"
                              small
                              to={`/school/${params.schoolId}/deliveries/${store.client_id}`}
                            >
                              {sourceLabel(store)}
                            </ButtonRouter>
                          ))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </TableExtended>
          </ModuleBody>
        </Module>
      </div>
    );

  // Stock events may hold the measure unit as a bare id (or not at all);
  // only an object with the symbol is renderable, so fall back to the
  // commodities catalogue
  const resolveMeasureUnit = (commodityId: string, ...candidates: any[]) =>
    candidates.find((unit) => unit && typeof unit === "object") ??
    commodityIndex[commodityId]?.measure_unit;

  const buildReadOnlyRows = (carryOverStock: any, incidents: any) =>
    Array.from(
      new Set([
        ...Object.keys(carryOverStock),
        ...Object.keys(incidents.quantityPerCommodity),
      ])
    ).map((commodityId) => ({
      commodity: commodityId,
      measureUnit: resolveMeasureUnit(
        commodityId,
        carryOverStock[commodityId]?.measure_unit,
        incidents.quantityPerCommodity[commodityId]?.measure_unit
      ),
    }));

  const readOnlyCommodities = buildReadOnlyRows(
    initialCarryOverStock,
    initialStockIncidents
  );

  const readOnlyPurchaseCommodities = buildReadOnlyRows(
    initialCarryOverPurchaseStock,
    initialPurchaseStockIncidents
  );

  const hasCarryOverStock =
    readOnlyCommodities.length > 0 || readOnlyPurchaseCommodities.length > 0;

  return (
    <ModalExtended
      hideClose={!isReadOnly}
      passiveModal={isReadOnly}
      onRequestSubmit={isReadOnly ? undefined : submitViaBtnRefClick}
      // While editing, outside clicks must not close the modal: clicks inside
      // the portaled zero-all confirmation bubble up through the React tree
      // and register here as outside clicks (the form closes via Cancel only)
      onRequestClose={
        isReadOnly ? () => closeModal(undefined, yearPath) : () => {}
      }
      onSecondarySubmit={() => closeModal(undefined, yearPath)}
      modalLabel={intl.formatMessage({
        id: "CarryOverEdit.modalLabel",
        defaultMessage: "School year",
      })}
      modalHeading={intl.formatMessage({
        id: "CarryOverEdit.modalHeading",
        defaultMessage: "Carry-over stock",
      })}
      primaryButtonText={intl.formatMessage({
        id: "CarryOverEdit.save",
        defaultMessage: "Save",
      })}
      primaryButtonDisabled={
        !!Object.keys(showQuantityExceededWarning.current).find(
          (key) => showQuantityExceededWarning.current[key] === true
        )
      }
      className="carry-over-edit"
    >
      {isReadOnly ? (
        <div className="wfp--form wfp--form-long">
          <Blockquote kind="info">
            <p>
              <FormattedMessage
                id="CarryOverEdit.readOnlyNotice"
                defaultMessage="The carry-over stock has been saved and synced. To make further adjustments, use the links below to open the corresponding incident, delivery or purchase."
              />
            </p>
          </Blockquote>
          {!hasCarryOverStock && (
            <Blockquote kind="warning">
              <p>
                <FormattedMessage
                  id="CarryOverEdit.noCarryOverStock"
                  defaultMessage="There is no remaining carry-over stock from the previous school year."
                />
              </p>
            </Blockquote>
          )}
          {renderReadOnlyTable(
            <FormattedMessage
              id="YearEdit.openingStock"
              defaultMessage="Opening stock for on-site school meals (WFP)"
            />,
            readOnlyCommodities,
            initialQuantities,
            incidentQuantities,
            adjustedQuantities,
            getIncidentsForCommodity(initialStockIncidents.incidentObjects),
            getSourceDeliveries,
            (store: any) => (
              <FormattedMessage
                id="CarryOverEdit.viewDelivery"
                defaultMessage="View delivery {waybill}"
                values={{ waybill: store.meta?.waybill_no || "" }}
              />
            )
          )}
          {renderReadOnlyTable(
            <FormattedMessage
              id="YearEdit.openingPurchaseStock"
              defaultMessage="Opening stock for purchases"
            />,
            readOnlyPurchaseCommodities,
            initialPurchaseQuantities,
            purchaseIncidentQuantities,
            adjustedPurchaseQuantities,
            getIncidentsForCommodity(
              initialPurchaseStockIncidents.incidentObjects
            ),
            getSourcePurchases,
            (store: any) => (
              <FormattedMessage
                id="CarryOverEdit.viewPurchase"
                defaultMessage="View purchase {receipt}"
                values={{ receipt: store.meta?.receipt_no || "" }}
              />
            )
          )}
        </div>
      ) : (
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="wfp--form wfp--form-long">
            <ErrorList errors={errors} />
            <Blockquote kind="warning">
              <p>
                <FormattedMessage
                  id="CarryOverEdit.info"
                  defaultMessage="The system is displaying a remaining stock from your previously recorded school year. You can adjust the stock levels per commodity here and record incidents."
                />
              </p>
            </Blockquote>
            {commodityRows.length > 0 && (
              <Repeater>
                <div className="carry-over-edit__section-header">
                  <RepeaterTitle>
                    <FormattedMessage
                      id="YearEdit.openingStock"
                      defaultMessage="Opening stock for on-site school meals (WFP)"
                    />
                  </RepeaterTitle>
                  <Button
                    type="button"
                    kind="secondary"
                    small
                    disabled={areAllQuantitiesZero(
                      "commodities",
                      commodityRows
                    )}
                    onClick={() => setZeroAllTarget("commodities")}
                  >
                    <FormattedMessage
                      id="CarryOverEdit.zeroAll"
                      defaultMessage="Set all to zero"
                    />
                  </Button>
                </div>
                {commodityRows.map((commodityRow: any, index) => (
                  <RepeaterItem
                    key={commodityRow.id}
                    disableRemove
                    className="delivery-edit__commodities"
                  >
                    <Controller
                      render={() => (
                        <CommodityTextView
                          className="wfp--react-select-container"
                          category={deliveryCategory}
                          key={`commodities[${index}].commodity`}
                          name={`commodities[${index}].commodity`}
                          value={commodityRow?.commodity}
                          control={control}
                        />
                      )}
                      name={`commodities[${index}].commodity`}
                      control={control}
                    />
                    <div className="quantity-container">
                      {showInitialQuantityLabel(
                        commodityRow?.commodity,
                        index,
                        "commodities",
                        initialQuantities,
                        adjustedQuantities
                      ) && (
                        <div className="wfp--form-item initial-stock-label">
                          <div className="wfp--label">
                            <FormattedMessage
                              id="YearEdit.initalCarryOverQuantity"
                              defaultMessage="Initial carry-over quantity"
                            />
                          </div>
                          <div className="inner-label-container">
                            <label>
                              {initialQuantities[commodityRow?.commodity] || 0}
                            </label>
                          </div>
                        </div>
                      )}
                      <Controller
                        render={({ onChange, onBlur, value }) => (
                          <QuantityInput
                            control={control}
                            value={value}
                            onChange={(e: any) => {
                              watch(`commodities[${index}].quantity`);
                              const quantity = toQuantity(e.target.value);
                              onChange(quantity);
                              setStockQuantity(
                                commodityRow.commodity,
                                index,
                                quantity,
                                initialQuantities[commodityRow.commodity] || 0,
                                adjustedQuantities[commodityRow.commodity] || 0,
                                updatedIncidentQuantities
                              );
                            }}
                            onBlur={onBlur}
                            labelText={
                              showInitialQuantityLabel(
                                commodityRow?.commodity,
                                index,
                                "commodities",
                                initialQuantities,
                                adjustedQuantities
                              ) ? (
                                <FormattedMessage
                                  id="YearEdit.adjustedCarryOverQuantity"
                                  defaultMessage="Adjusted carry-over quantity"
                                />
                              ) : (
                                <FormattedMessage
                                  id="DeliveriesEdit.quantity"
                                  defaultMessage="Quantity"
                                />
                              )
                            }
                            setValue={setValue}
                            name={`commodities[${index}].quantity`}
                            invalid={errors[`commodities[${index}].quantity`]}
                            invalidText={
                              errors[`commodities[${index}].quantity`]?.message
                            }
                            measureUnit={watch(
                              `commodities[${index}].measure_unit`
                            )}
                            allowZero={true}
                          />
                        )}
                        control={control}
                        name={`commodities[${index}].quantity`}
                      />
                      {(toQuantity(
                        updatedIncidentQuantities.current[
                          commodityRow?.commodity
                        ]
                      ) > 0 ||
                        showQuantityExceededWarning.current[index] ===
                          true) && (
                        <div className="wfp--form-item incidents-quantity-label">
                          <div className="wfp--label">
                            <FormattedMessage
                              id="YearEdit.incidentsQuantity"
                              defaultMessage="Incidents quantity"
                            />
                          </div>
                          <div className="inner-label-container">
                            <label>
                              {toQuantity(
                                updatedIncidentQuantities.current[
                                  commodityRow?.commodity
                                ]
                              )}
                            </label>
                          </div>
                        </div>
                      )}
                    </div>
                    {showIncidentForCommodity.current[index] === true && (
                      <div>
                        <Blockquote kind="warning">
                          <p>
                            <FormattedMessage
                              id="YearEdit.incidentCreationOpeningStock"
                              defaultMessage="An incident will be created as you have inputted a value less than the carry-over stock."
                            />
                          </p>
                        </Blockquote>
                        <YearEditIncidentReasons
                          control={control}
                          errors={errors}
                          index={index}
                          register={register}
                        />
                      </div>
                    )}
                    {showQuantityExceededWarning.current[index] === true && (
                      <div>
                        <Blockquote kind="error">
                          <p>
                            <FormattedMessage
                              id="YearEdit.quantityExceededWarning"
                              defaultMessage="You cannot increase the quantity of a carry-over stock."
                            />
                          </p>
                        </Blockquote>
                      </div>
                    )}
                  </RepeaterItem>
                ))}
              </Repeater>
            )}
            {purchaseCommodityRows.length > 0 && (
              <Repeater>
                <div className="carry-over-edit__section-header">
                  <RepeaterTitle>
                    <FormattedMessage
                      id="YearEdit.openingPurchaseStock"
                      defaultMessage="Opening stock for purchases"
                    />
                  </RepeaterTitle>
                  <Button
                    type="button"
                    kind="secondary"
                    small
                    disabled={areAllQuantitiesZero(
                      "purchaseCommodities",
                      purchaseCommodityRows
                    )}
                    onClick={() => setZeroAllTarget("purchaseCommodities")}
                  >
                    <FormattedMessage
                      id="CarryOverEdit.zeroAll"
                      defaultMessage="Set all to zero"
                    />
                  </Button>
                </div>
                {purchaseCommodityRows.map((commodityRow: any, index) => (
                  <RepeaterItem
                    key={commodityRow.id}
                    disableRemove
                    className="delivery-edit__commodities"
                  >
                    <Controller
                      render={() => (
                        <CommodityTextView
                          className="wfp--react-select-container"
                          category={purchaseDetailCategory}
                          key={`purchaseCommodities[${index}].commodity`}
                          name={`purchaseCommodities[${index}].commodity`}
                          value={commodityRow?.commodity}
                          control={control}
                        />
                      )}
                      name={`purchaseCommodities[${index}].commodity`}
                      control={control}
                    />
                    <div className="quantity-container">
                      {showInitialQuantityLabel(
                        commodityRow?.commodity,
                        index,
                        "purchaseCommodities",
                        initialPurchaseQuantities,
                        adjustedPurchaseQuantities
                      ) && (
                        <div className="wfp--form-item initial-stock-label">
                          <div className="wfp--label">
                            <FormattedMessage
                              id="YearEdit.initalCarryOverPurchaseQuantity"
                              defaultMessage="Initial carry-over purchase quantity"
                            />
                          </div>
                          <div className="inner-label-container">
                            <label>
                              {initialPurchaseQuantities[
                                commodityRow?.commodity
                              ] || 0}
                            </label>
                          </div>
                        </div>
                      )}
                      <Controller
                        render={({ onChange, onBlur, value }) => (
                          <QuantityInput
                            control={control}
                            value={value}
                            onChange={(e: any) => {
                              watch(`purchaseCommodities[${index}].quantity`);
                              const quantity = toQuantity(e.target.value);
                              onChange(quantity);
                              setStockQuantity(
                                commodityRow.commodity,
                                `purchase_${index}`,
                                quantity,
                                initialPurchaseQuantities[
                                  commodityRow.commodity
                                ] || 0,
                                adjustedPurchaseQuantities[
                                  commodityRow.commodity
                                ] || 0,
                                updatedPurchaseIncidentQuantities
                              );
                            }}
                            onBlur={onBlur}
                            labelText={
                              showInitialQuantityLabel(
                                commodityRow?.commodity,
                                index,
                                "purchaseCommodities",
                                initialPurchaseQuantities,
                                adjustedPurchaseQuantities
                              ) ? (
                                <FormattedMessage
                                  id="YearEdit.adjustedCarryOverPurchaseQuantity"
                                  defaultMessage="Adjusted carry-over purchase quantity"
                                />
                              ) : (
                                <FormattedMessage
                                  id="DeliveriesEdit.quantity"
                                  defaultMessage="Quantity"
                                />
                              )
                            }
                            setValue={setValue}
                            name={`purchaseCommodities[${index}].quantity`}
                            invalid={
                              errors[`purchaseCommodities[${index}].quantity`]
                            }
                            invalidText={
                              errors[`purchaseCommodities[${index}].quantity`]
                                ?.message
                            }
                            measureUnit={watch(
                              `purchaseCommodities[${index}].measure_unit`
                            )}
                            allowZero={true}
                          />
                        )}
                        control={control}
                        name={`purchaseCommodities[${index}].quantity`}
                      />
                      {(toQuantity(
                        updatedPurchaseIncidentQuantities.current[
                          commodityRow?.commodity
                        ]
                      ) > 0 ||
                        showQuantityExceededWarning.current[
                          `purchase_${index}`
                        ] === true) && (
                        <div className="wfp--form-item incidents-quantity-label">
                          <div className="wfp--label">
                            <FormattedMessage
                              id="YearEdit.purchaseIncidentsQuantity"
                              defaultMessage="Purchase incidents quantity"
                            />
                          </div>
                          <div className="inner-label-container">
                            <label>
                              {toQuantity(
                                updatedPurchaseIncidentQuantities.current[
                                  commodityRow?.commodity
                                ]
                              )}
                            </label>
                          </div>
                        </div>
                      )}
                    </div>
                    {showIncidentForCommodity.current[`purchase_${index}`] ===
                      true && (
                      <div>
                        <Blockquote kind="warning">
                          <p>
                            <FormattedMessage
                              id="YearEdit.incidentCreationOpeningPurchaseStock"
                              defaultMessage="A purchase incident will be created as you have inputted a value less than the carry-over purchase stock."
                            />
                          </p>
                        </Blockquote>
                        <YearEditIncidentReasons
                          control={control}
                          errors={errors}
                          index={index}
                          register={register}
                          isPurchase={true}
                        />
                      </div>
                    )}
                    {showQuantityExceededWarning.current[
                      `purchase_${index}`
                    ] === true && (
                      <div>
                        <Blockquote kind="error">
                          <p>
                            <FormattedMessage
                              id="YearEdit.quantityExceededPurchaseWarning"
                              defaultMessage="You cannot increase the quantity of a carry-over purchase stock."
                            />
                          </p>
                        </Blockquote>
                      </div>
                    )}
                  </RepeaterItem>
                ))}
              </Repeater>
            )}
            {!hasCarryOverStock && (
              <Blockquote kind="warning">
                <p>
                  <FormattedMessage
                    id="CarryOverEdit.noCarryOverStock"
                    defaultMessage="There is no remaining carry-over stock from the previous school year."
                  />
                </p>
              </Blockquote>
            )}
          </div>
          <button className="hidden-btn" ref={submitBtnRef as any} />
        </form>
      )}
      {!isReadOnly &&
        // Portal to the document body, otherwise the confirmation is trapped
        // in the management modal's stacking context and renders behind it
        createPortal(
          <Modal
            open={zeroAllTarget !== null}
            className="carry-over-edit__zero-all-modal"
            modalHeading={intl.formatMessage({
              id: "CarryOverEdit.zeroAll",
              defaultMessage: "Set all to zero",
            })}
            primaryButtonText={intl.formatMessage({
              id: "CarryOverEdit.zeroAll",
              defaultMessage: "Set all to zero",
            })}
            secondaryButtonText={intl.formatMessage({
              id: "ModalExtended.cancel",
              defaultMessage: "Cancel",
            })}
            onRequestSubmit={confirmZeroAll}
            onSecondarySubmit={() => setZeroAllTarget(null)}
            onRequestClose={() => setZeroAllTarget(null)}
          >
            <Blockquote kind="warning">
              <p>
                <FormattedMessage
                  id="CarryOverEdit.zeroAllConfirmation"
                  defaultMessage="All carry-over quantities will be set to zero and you will need to select an incident type and reason for each commodity."
                />
              </p>
            </Blockquote>
          </Modal>,
          document.body
        )}
    </ModalExtended>
  );
};

export default CarryOverEdit;
